/**
 * useUiContextReporter — reports the user's current route + URL filter
 * to the backend over the WebSocket so the chat agent's
 * `yagni_ui_readContext` tool can ground its replies.
 *
 * Spec: docs/superpowers/specs/2026-05-13-unified-front-with-desks-and-chat-onboarding-design.md §4.3
 *
 * Debounce: 200ms after the route or search-params change. The server
 * additionally enforces a 1Hz floor per (user, workspace), so a burst of
 * client-side navigations collapses to at most one report per second on
 * the receiving side.
 */

import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type { WsClientMessage } from "@yagni/shared";

export function useUiContextReporter(
  send: (message: WsClientMessage) => void,
  enabled = true,
): void {
  const location = useLocation();
  const [params] = useSearchParams();

  useEffect(() => {
    if (!enabled) return;
    // Build the search snapshot from URL params. selectedFilter is the
    // primary filter the agent cares about. Prefer the center artifact over
    // its containing Team/lane when both are present.
    const selectedFilter =
      params.get("workItem") ??
      params.get("item") ??
      params.get("event") ??
      params.get("team") ??
      params.get("folder") ??
      params.get("desk") ??
      params.get("id") ??
      null;
    const search: Record<string, string> = {};
    params.forEach((value, key) => {
      search[key] = value;
    });

    const handle = window.setTimeout(() => {
      send({
        type: "ui_context.updated",
        payload: {
          route: location.pathname,
          search,
          selectedFilter,
        },
      });
    }, 200);

    return () => window.clearTimeout(handle);
  }, [enabled, location.pathname, params, send]);
}
